import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBagIcon, StoreIcon, GlobeIcon, PlusIcon, EyeIcon, EyeOffIcon, PackageIcon, DollarSignIcon, MoreHorizontalIcon, CreditCardIcon, TrashIcon, EditIcon, Share2Icon, SearchIcon, LinkIcon, CopyIcon, QrCodeIcon, ChevronRightIcon, XIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
// Shop type definition
export interface Shop {
  id: string;
  name: string;
  description: string;
  logo: string;
  color: string;
  isOpen: boolean;
  channels: ('pos' | 'ecommerce')[];
  productsCount: number;
  revenue: {
    total: number;
    currency: string;
    growth: number;
  };
  ordersCount: number;
}
export function Shops() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'open' | 'closed'>('all');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const [shareModalShopId, setShareModalShopId] = useState<string | null>(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const [shops, setShops] = useState<Shop[]>([{
    id: '1',
    name: 'Beach Shop',
    description: 'Beachwear and accessories for summer holidays',
    logo: '•',
    color: '#f8d7d7',
    isOpen: true,
    channels: ['pos', 'ecommerce'],
    productsCount: 48,
    revenue: {
      total: 12580,
      currency: 'THB',
      growth: 15.3
    },
    ordersCount: 124
  }, {
    id: '2',
    name: 'Island Shop',
    description: 'Local island products and souvenirs',
    logo: '+',
    color: '#e1f5fe',
    isOpen: true,
    channels: ['ecommerce'],
    productsCount: 32,
    revenue: {
      total: 8750,
      currency: 'THB',
      growth: 8.7
    },
    ordersCount: 86
  }, {
    id: '3',
    name: 'ABC',
    description: 'Convenience store with everyday essentials',
    logo: 'A',
    color: '#e8f5e9',
    isOpen: false,
    channels: ['pos'],
    productsCount: 156,
    revenue: {
      total: 24680,
      currency: 'THB',
      growth: -2.4
    },
    ordersCount: 312
  }]);
  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleShopStatus = (shopId: string) => {
    setShops(shops.map(shop => shop.id === shopId ? {
      ...shop,
      isOpen: !shop.isOpen
    } : shop));
    setContextMenuOpenId(null);
  };
  const deleteShop = (shopId: string) => {
    if (confirm('Are you sure you want to delete this shop? This action cannot be undone.')) {
      setShops(shops.filter(shop => shop.id !== shopId));
    }
    setContextMenuOpenId(null);
  };
  const copyShopUrl = (shopId: string) => {
    const shopUrl = `https://yourwebsite.com/shop/${shopId}`;
    navigator.clipboard.writeText(shopUrl);
  };
  const filteredShops = shops.filter(shop => {
    const matchesSearch = searchQuery === '' || shop.name.toLowerCase().includes(searchQuery.toLowerCase()) || shop.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || activeTab === 'open' && shop.isOpen || activeTab === 'closed' && !shop.isOpen;
    return matchesSearch && matchesTab;
  });
  const totalRevenue = shops.reduce((sum, s) => sum + s.revenue.total, 0);
  const totalProducts = shops.reduce((sum, s) => sum + s.productsCount, 0);
  const totalOrders = shops.reduce((sum, s) => sum + s.ordersCount, 0);
  const getStatusBadge = (isOpen: boolean) => {
    return isOpen ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800" data-id="element-11041">
        Open
      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-800" data-id="element-11042">
        Closed
      </span>;
  };
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-11043">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-11044">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-11045">Shops</h1>
        <div className="flex items-center gap-3" data-id="element-11046">
          <button onClick={() => navigate('/shop-details?new=true')} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11047">
            Add shop
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-11048">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-11049">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-11050">
            <StoreIcon size={16} className="text-gray-500" data-id="element-11051" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-11052">
              Total Shops
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-11053">
            <div className="text-2xl font-bold text-gray-900" data-id="element-11054">
              {shops.length}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-11055">
            {shops.filter(s => s.isOpen).length} open ·{' '}
            {shops.filter(s => !s.isOpen).length} closed
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-11056">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-11057">
            <DollarSignIcon size={16} className="text-gray-500" data-id="element-11058" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-11059">
              Total Revenue
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-11060">
            <div className="text-2xl font-bold text-gray-900" data-id="element-11061">
              THB {totalRevenue.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-11062">
            Across {shops.length} shops
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-11063">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-11064">
            <PackageIcon size={16} className="text-gray-500" data-id="element-11065" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-11066">
              Total Products
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-11067">
            <div className="text-2xl font-bold text-gray-900" data-id="element-11068">
              {totalProducts}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-11069">
            All shops combined
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-11070">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-11071">
            <ShoppingBagIcon size={16} className="text-gray-500" data-id="element-11072" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-11073">
              Total Orders
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-11074">
            <div className="text-2xl font-bold text-gray-900" data-id="element-11075">
              {totalOrders}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-11076">
            All shops combined
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11077">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-11078">
          {/* Tabs */}
          <div className="flex space-x-6" data-id="element-11079">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'open',
            label: 'Open'
          }, {
            id: 'closed',
            label: 'Closed'
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as 'all' | 'open' | 'closed')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-11080">
                {tab.label}
              </button>)}
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-64" data-id="element-11081">
            <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11082" />
            <input type="text" placeholder="Search shops..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11083" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-11084">
          <table className="w-full text-left border-collapse" data-id="element-11085">
            <thead data-id="element-11086">
              <tr className="border-b border-gray-100" data-id="element-11087">
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11088">
                  Shop
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11089">
                  Channels
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11090">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11091">
                  Products
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11092">
                  Revenue
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11093">
                  Orders
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-11094">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-11095">
              {filteredShops.map(shop => <tr key={shop.id} onClick={() => navigate(`/shop-details?id=${shop.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-11096">
                  <td className="px-6 py-4" data-id="element-11097">
                    <div className="flex items-center gap-3" data-id="element-11098">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: shop.color
                  }} data-id="element-11099">
                        <span className="text-base font-bold text-red-600" data-id="element-11100">
                          {shop.logo}
                        </span>
                      </div>
                      <div data-id="element-11101">
                        <div className="text-sm font-medium text-gray-900 group-hover:text-[#F7941D] transition-colors" data-id="element-11102">
                          {shop.name}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5" data-id="element-11103">
                          {shop.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-11104">
                    <div className="flex items-center gap-1.5" data-id="element-11105">
                      {shop.channels.includes('pos') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-11106">
                          <CreditCardIcon size={11} className="mr-1" data-id="element-11107" />
                          POS
                        </span>}
                      {shop.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-11108">
                          <GlobeIcon size={11} className="mr-1" data-id="element-11109" />
                          E-Commerce
                        </span>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-11110">{getStatusBadge(shop.isOpen)}</td>
                  <td className="px-6 py-4" data-id="element-11111">
                    <div className="text-sm font-medium text-gray-900" data-id="element-11112">
                      {shop.productsCount}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-11113">
                    <div className="text-sm font-medium text-gray-900" data-id="element-11114">
                      THB {shop.revenue.total.toLocaleString('en-US')}
                    </div>
                    <div className={`text-xs mt-0.5 ${shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-11115">
                      {shop.revenue.growth >= 0 ? '+' : ''}
                      {shop.revenue.growth}%
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-11116">
                    <div className="text-sm font-medium text-gray-900" data-id="element-11117">
                      {shop.ordersCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right relative" data-id="element-11118">
                    <div ref={contextMenuOpenId === shop.id ? contextMenuRef : null} data-id="element-11119">
                      <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                    e.stopPropagation();
                    setContextMenuOpenId(contextMenuOpenId === shop.id ? null : shop.id);
                  }} data-id="element-11120">
                        <MoreHorizontalIcon size={18} data-id="element-11121" />
                      </button>

                      {contextMenuOpenId === shop.id && <>
                          <div className="fixed inset-0 z-10" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(null);
                    }} data-id="element-11122" />
                          <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-11123">
                            <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/shop-details?id=${shop.id}`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11124">
                              <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-11125" />
                              View Details
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/shop-details?id=${shop.id}&edit=true`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11126">
                              <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-11127" />
                              Edit Shop
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        setShareModalShopId(shop.id);
                        setShowQRCode(false);
                        setContextMenuOpenId(null);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11128">
                              <Share2Icon size={16} className="mr-3 text-gray-400" data-id="element-11129" />
                              Share Shop
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        toggleShopStatus(shop.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11130">
                              {shop.isOpen ? <>
                                  <EyeOffIcon size={16} className="mr-3 text-gray-400" data-id="element-11131" />
                                  Close Shop
                                </> : <>
                                  <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-11132" />
                                  Open Shop
                                </>}
                            </button>
                            <div className="border-t border-gray-100 my-1" data-id="element-11133" />
                            <button onClick={e => {
                        e.stopPropagation();
                        deleteShop(shop.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-11134">
                              <TrashIcon size={16} className="mr-3" data-id="element-11135" />
                              Delete Shop
                            </button>
                          </div>
                        </>}
                    </div>
                  </td>
                </tr>)}
              {filteredShops.length === 0 && <tr data-id="element-11136">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-11137">
                    No shops found matching your criteria.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Share Modal */}
      {shareModalShopId && (() => {
      const shop = shops.find(s => s.id === shareModalShopId);
      if (!shop) return null;
      return <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-id="element-11138">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200" data-id="element-11139">
                <div className="flex justify-between items-center mb-5" data-id="element-11140">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-11141">
                    Share {shop.name}
                  </h3>
                  <button onClick={() => setShareModalShopId(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" data-id="element-11142">
                    <XIcon size={18} data-id="element-11143" />
                  </button>
                </div>
                {showQRCode ? <div className="text-center" data-id="element-11144">
                    <div className="bg-white p-4 rounded-lg inline-block mb-4 border border-gray-200" data-id="element-11145">
                      <div className="w-48 h-48 bg-gray-50 rounded flex items-center justify-center" data-id="element-11146">
                        <QrCodeIcon size={80} className="text-gray-300" data-id="element-11147" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-11148">
                      Scan this QR code to visit {shop.name}
                    </p>
                    <button onClick={() => setShowQRCode(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium transition-colors" data-id="element-11149">
                      Back to share options
                    </button>
                  </div> : <>
                    <div className="space-y-3 mb-5" data-id="element-11150">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-11151">
                        <div className="flex items-center" data-id="element-11152">
                          <LinkIcon size={16} className="text-gray-400 mr-2" data-id="element-11153" />
                          <span className="text-sm text-gray-600 truncate max-w-[220px]" data-id="element-11154">
                            https://yourwebsite.com/shop/{shareModalShopId}
                          </span>
                        </div>
                        <button onClick={() => copyShopUrl(shareModalShopId)} className="p-1.5 text-[#F7941D] hover:bg-[#F7941D]/10 rounded-md transition-colors" data-id="element-11155">
                          <CopyIcon size={16} data-id="element-11156" />
                        </button>
                      </div>
                      <button onClick={() => setShowQRCode(true)} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors" data-id="element-11157">
                        <div className="flex items-center" data-id="element-11158">
                          <QrCodeIcon size={16} className="text-gray-400 mr-2" data-id="element-11159" />
                          <span className="text-sm text-gray-600" data-id="element-11160">
                            Generate QR Code
                          </span>
                        </div>
                        <ChevronRightIcon size={16} className="text-gray-400" data-id="element-11161" />
                      </button>
                    </div>
                    <div className="flex justify-end" data-id="element-11162">
                      <button onClick={() => setShareModalShopId(null)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium transition-colors" data-id="element-11163">
                        Close
                      </button>
                    </div>
                  </>}
              </div>
            </div>;
    })()}
    </div>;
}